import type { ReactNode } from "react";
import type { MotdEntry } from "./types.js";

/**
 * Navy-header status document — same visual language as the Monday status
 * Gmail HTML template (RR\\ID · kicker + white headline + structured body).
 */
export function MotdDocument({
  entry,
  footer,
  className = "",
  bodyClassName = "",
}: {
  entry: MotdEntry;
  footer?: ReactNode;
  className?: string;
  /** Extra classes on the scrollable body (e.g. max-height on login). */
  bodyClassName?: string;
}): JSX.Element {
  const kicker = entry.kicker ?? "RR\\ID";
  const html = entry.bodyHtml?.trim();

  return (
    <article
      className={`overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}
      aria-label="Message of the day"
    >
      <header className="bg-[#0f2942] px-5 py-4 sm:px-7 sm:py-5">
        <p className="text-[13px] font-medium uppercase tracking-[0.04em] text-[#9ec5e8]">{kicker}</p>
        <h2 className="mt-1.5 text-lg font-semibold leading-snug text-white sm:text-[22px]">
          {entry.headline}
        </h2>
      </header>
      <div className={`px-5 py-5 sm:px-7 sm:py-7 ${bodyClassName}`}>
        {html ? (
          <div className="motd-prose" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-800">{entry.body}</p>
        )}
      </div>
      {footer ? (
        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-500 sm:px-7">
          {footer}
        </footer>
      ) : null}
    </article>
  );
}
